import { useGetRecoveryConfigQuery } from 'api/recoveryAi'
import NpsTransitionModal, {
  NpsTransitionModalVariant,
} from 'components/NpsTransitionModal'
import { useMerchantFeedbackEnabled } from 'hooks/useCurrentMerchant'
import useFlag from 'hooks/useFlag'
import { useParentNavAlwaysEnabled } from 'hooks/useRbacNavCheck'
import { buildTranslate } from 'locales'
import React, { useState } from 'react'
// Deviates from the no-scene-boundary-imports rule: recoveryStatus is a
// dependency-free predicate over the api/recoveryAi config this file already
// fetches, so duplicating it into components/ would fork the definition of
// "Recovery is active". Flagged for review.
import { isRecoveryActive } from 'scenes/RecoveryAI/recoveryStatus'
import { useAbility } from 'utilities/ability'
import AccordionButton from '../Bar/AccordionButton'
import MenuItem from '../Bar/MenuItem'
import OperationsCenterContent from './Content'

const t = buildTranslate('nav.operations_center_menu')

type PropsT = {
  isLeftNav?: boolean
  onClick?: () => void
  paths: string[]
}

const OperationsCenter = ({
  isLeftNav = false,
  onClick = () => {},
  paths,
}: PropsT) => {
  const ability = useAbility()
  const parentAlwaysEnabled = useParentNavAlwaysEnabled()
  const menuDisabled =
    !parentAlwaysEnabled &&
    ability.cannot('access', 'Feedback') &&
    ability.cannot('access', 'NpsReports')

  const feedbackEnabled = useMerchantFeedbackEnabled()
  // The "NPS Feedback" item is hidden when the merchant has feedback disabled
  // (nothing to transition away from).
  const npsFeedbackEntryHidden = !feedbackEnabled

  // Skipped when the flag is off, which reads as "not active" below --
  // correctly steering those merchants to the pre-launch modal (when there is
  // one to steer them to -- see npsTransitionVariant).
  //
  // Also skipped without `access` on MerchantSettings: the config GET is
  // `rbac_authorize!('view', 'merchant_settings')` server-side, and the nav is
  // rendered on every page -- including /recovery_ai's promo page, which is
  // open to every authenticated merchant user -- so an unskipped call would
  // 403 and raise a danger toast (api/index.ts) app-wide for those viewers.
  // Degrading to recoveryActive === false gives them the 'pre_launch' variant,
  // which is the correct read: they cannot see the RecoveryAI console, so
  // announcing it as live would point them at a surface they are not allowed
  // to open.
  const recoveryAiEnabled = useFlag('recovery-ai', false)
  const { data: recoveryConfig } = useGetRecoveryConfigQuery(undefined, {
    skip:
      !recoveryAiEnabled ||
      npsFeedbackEntryHidden ||
      ability.cannot('access', 'MerchantSettings'),
  })
  const recoveryActive = isRecoveryActive(recoveryConfig)
  // The report is universally reachable post-GA (Navbar/OperationsCenter/
  // Content.tsx), so every merchant now has something to announce: RecoveryAI
  // going live, or -- short of that -- the Orders 👍/👎 report itself.
  const npsTransitionVariant: NpsTransitionModalVariant = recoveryActive
    ? 'active'
    : 'pre_launch'
  const [isNpsModalOpen, setIsNpsModalOpen] = useState(false)

  const Parent = isLeftNav ? AccordionButton : MenuItem
  return (
    <>
      <Parent
        disabled={menuDisabled}
        paths={paths}
        buttonContents={t('button')}
      >
        <OperationsCenterContent
          isLeftNav={isLeftNav}
          onClick={onClick}
          onNpsClick={() => setIsNpsModalOpen(true)}
        />
      </Parent>
      {/* Rendered as a SIBLING of Parent, not a descendant: MenuItem's
          Dropdown reopens itself whenever a focus event bubbles up from
          inside it, and React bubbles portalled events (this Modal renders
          via baseui's Layer, a portal to document.body) through the React
          tree, not the DOM tree. A modal nested inside MenuItem fights its
          own close button as baseui's focus-lock keeps re-triggering that. */}
      {!npsFeedbackEntryHidden && (
        <NpsTransitionModal
          isOpen={isNpsModalOpen}
          onClose={() => setIsNpsModalOpen(false)}
          variant={npsTransitionVariant}
        />
      )}
    </>
  )
}

export default OperationsCenter
